import type {EcommerceSalesChannel} from '@/types/salesChannels';
import type {PrestaSalesChannel} from '@/types/salesChannels/prestaSalesChannels';
import type {
  DelegationApiKeyWithoutSecretCredentials,
  PostRegisterApiKeyWithoutSecretResponse,
} from '@myparcel-frontend/delegation-service';
import type {
  CreateSalesChannelRequest,
  ImportProductsRequest,
  PostRequestDelegationResponse,
  RenameSalesChannelRequest,
} from '@myparcel-frontend/presta-ecommerce-service';
import {useDelegationService} from '@myparcel-frontend/delegation-service';
import {usePrestaEcommerceService} from '@myparcel-frontend/presta-ecommerce-service';
import {attemptAsync} from 'es-toolkit';
import {useSalesChannelStore} from '@/store/salesChannelStore/salesChannelStore';
import logToMonitor from '@/utils/logging';

export default function usePrestaEcommerceSalesChannels() {
  const salesChannelStore = useSalesChannelStore();

  const getPrestaSalesChannels = async (shopIds: string[]): Promise<PrestaSalesChannel[] | []> => {
    const client = usePrestaEcommerceService();

    const [error, response] = await attemptAsync(() => client.getSalesChannels(shopIds));

    if (error) {
      logToMonitor(error, {
        _myparcel_extra_info: 'Fetching PrestaShop sales channels failed in usePresta composable',
      });

      throw error;
    }

    const result: PrestaSalesChannel[] = [];

    if (!response || !response?.results?.length) {
      return result;
    }

    for (const salesChannel of response.results) {
      const prestaSalesChannel: PrestaSalesChannel = {
        ...salesChannel,
        ecommercePlatform: 'PRESTA',
      };

      const newSalesChannel = salesChannelStore.addSalesChannel(prestaSalesChannel);

      result.push(newSalesChannel);
    }

    return result;
  };

  const createPrestaSalesChannel = async (data: CreateSalesChannelRequest) => {
    const client = usePrestaEcommerceService();

    const [error, response] = await attemptAsync(() => client.createSalesChannel(data));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Creating PrestaShop sales channel failed in usePresta composable',
        _myparcel_sales_channel_payload: data,
      });

      throw error;
    }

    const newPrestaSalesChannel: PrestaSalesChannel = {
      ...response,
      ecommercePlatform: 'PRESTA',
    };

    salesChannelStore.addSalesChannel(newPrestaSalesChannel);

    return response;
  };

  const renameSalesChannel = async (data: RenameSalesChannelRequest) => {
    const client = usePrestaEcommerceService();

    const [error, response] = await attemptAsync(() => client.renameSalesChannel(data));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Renaming PrestaShop sales channel failed in usePresta composable',
        _myparcel_sales_channel_id: data.salesChannelId,
        _myparcel_new_name: data.name,
      });

      throw error;
    }

    const updatedPrestaSalesChannel: PrestaSalesChannel = {
      ...response,
      ecommercePlatform: 'PRESTA',
    };

    salesChannelStore.updateSalesChannel(updatedPrestaSalesChannel);

    return response;
  };

  const removePrestaSalesChannel = async (salesChannelId: string) => {
    const client = usePrestaEcommerceService();

    const [error, response] = await attemptAsync(() => client.removeSalesChannel(salesChannelId));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Removing PrestaShop sales channel failed in usePresta composable',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }

    salesChannelStore.removeSalesChannel(salesChannelId);

    return response;
  };

  const enableOrderLoading = async (salesChannelId: string) => {
    const client = usePrestaEcommerceService();

    const [error, response] = await attemptAsync(() => client.enableOrderLoading(salesChannelId));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Enabling order loading for PrestaShop sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      return undefined;
    }

    salesChannelStore.enableOrderLoading(salesChannelId);

    return response;
  };

  const disableOrderLoading = async (salesChannelId: string) => {
    const client = usePrestaEcommerceService();

    const [error, response] = await attemptAsync(() => client.disableOrderLoading(salesChannelId));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Disabling order loading for PrestaShop sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      return undefined;
    }

    salesChannelStore.disableOrderLoading(salesChannelId);

    return response;
  };

  const fetchPrestaSalesChannelById = async (salesChannelId: string): Promise<PrestaSalesChannel | undefined> => {
    const client = usePrestaEcommerceService();

    const [error, response] = await attemptAsync(() => client.getSalesChannelById(salesChannelId));

    if (error) {
      logToMonitor(error, {
        _myparcel_extra_info: 'Fetching PrestaShop sales channel by id failed',
        _myparcel_sales_channel: salesChannelId,
      });

      throw error;
    }

    const prestaSalesChannel: PrestaSalesChannel = {
      ...response,
      ecommercePlatform: 'PRESTA',
    };

    salesChannelStore.addSalesChannel(prestaSalesChannel);

    return prestaSalesChannel;
  };
  /* c8 ignore stop */

  const requestDelegation = async (salesChannelId: string): Promise<PostRequestDelegationResponse | undefined> => {
    const client = usePrestaEcommerceService();

    const response = await client.requestDelegation(salesChannelId);

    salesChannelStore.updateSalesChannelToDelegationRequested(response.id, {
      authorizeUrl: response.authorizeUrl,
    });

    return response;
  };

  const createApiKeyCredentials = async (
    salesChannelId: EcommerceSalesChannel['id'],
    credentials: DelegationApiKeyWithoutSecretCredentials,
  ): Promise<PostRegisterApiKeyWithoutSecretResponse> => {
    const client = useDelegationService();

    const result = await client.createDelegationRequestsApiKeyWithoutSecret({
      apiKey: credentials,
      delegateeId: {
        delegatee: 'PRESTA',
        id: salesChannelId,
      },
    });

    return result;
  };

  const importPrestaProducts = async (salesChannelIds: ImportProductsRequest['salesChannelIds']) => {
    const client = usePrestaEcommerceService();

    const [error, response] = await attemptAsync(() => client.importProducts(salesChannelIds));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Importing PrestaShop products failed in usePresta composable',
        _myparcel_sales_channel_ids: salesChannelIds,
      });
      throw error;
    }

    return response;
  };

  return {
    getPrestaSalesChannels,
    createPrestaSalesChannel,
    removePrestaSalesChannel,
    renameSalesChannel,
    enableOrderLoading,
    disableOrderLoading,
    requestDelegation,
    createApiKeyCredentials,
    fetchPrestaSalesChannelById,
    importPrestaProducts,
  };
}
